import { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { KycSuccessModal } from "../KycSuccessModal";
import { KycSuspectModal } from "../KycSuspectModal";
import { Button, Icon, Modal } from "../../common";
import { DecisionDialog } from "../../common/DecisionDialog";
import { UsdKYCFAQ, UsdKYCInitialInstructions } from "../../content";
import { selectCurrentUser } from "../../../store/reducers/user";
import { useGetUserQuery } from "../../../store/services/user";
import { AccountType, KYCStatus } from "../../../types/UserTypes";
import { UsdKycForm } from "../../forms/usdKycForm";

type Props = {
  isOpen: boolean;
  close: () => void;
};

export const UsdKycModal: FC<Props> = ({ isOpen, close }) => {
  const [status, setStatus] = useState<KYCStatus>(KYCStatus.NONE);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [letsVerifyMe, setLetsVerifyMe] = useState(false);
  const { refetch } = useGetUserQuery();

  const {
    account_type,
    usd_kyc_complete,
    usd_kyc_complete_and_approved,
    usd_tos_accepted,
  } = useSelector(selectCurrentUser);

  const title =
    account_type === AccountType.CONTRACTOR
      ? "Get verified for USD"
      : "Get your business verified for USD";

  const closeModals = () => {
    if (status === KYCStatus.STARTED) {
      setShowConfirmCloseModal(true);
      return;
    }

    confirmCloseModal();
  };

  const confirmCloseModal = useCallback(() => {
    close();
    refetch();
    setShowConfirmCloseModal(false);
    setStatus(KYCStatus.NONE);
  }, [close, refetch]);

  if (usd_kyc_complete_and_approved) {
    return <KycSuccessModal isOpen={isOpen} close={closeModals} />;
  }

  if (usd_kyc_complete && !usd_kyc_complete_and_approved) {
    return <KycSuspectModal isOpen={isOpen} close={closeModals} />;
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        close={closeModals}
        title={title}
        subtitle="to get your USD account."
      >
        <div className="flex flex-col gap-16">
          {!letsVerifyMe ? (
            <>
              <UsdKYCInitialInstructions />

              <div className="flex justify-center">
                <Button
                  onClick={() => setLetsVerifyMe(true)}
                  variant={Button.variant.PRIMARY}
                >
                  Let's verify me
                </Button>
              </div>
            </>
          ) : !usd_kyc_complete || !usd_tos_accepted ? (
            <UsdKycForm />
          ) : (
            <>Error on KYC State.</>
          )}

          <hr className="border-gray-100" />

          {!letsVerifyMe && (
            <>
              <UsdKYCFAQ />

              <div className="flex justify-center">
                <Button className="contact-support-btn">
                  Get Help{" "}
                  <Icon
                    icon="support_agent"
                    size={24}
                    className="text-gray-500"
                    disableFill
                  />
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <DecisionDialog
        title="Are you sure you want to stop verification?"
        isOpen={showConfirmCloseModal}
        cancel={{
          label: "Back to verification",
          onClick: () => setShowConfirmCloseModal(false),
        }}
        proceed={{
          label: "Yes, stop verification",
          onClick: confirmCloseModal,
        }}
      >
        The progress could be lost
      </DecisionDialog>
    </>
  );
};
